import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { MessageKey } from "../../shared/i18n";
import { usePreferencesStore, type ScannerDisplay } from "../../shared/store/preferences";
import { ScanOverlay } from "./ScanOverlay";

export interface ScanStageProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  scanning: boolean;
  cameraError: boolean;
  /** Accessible name for the video (dialog copy differs from `/scan`). */
  ariaLabel: string;
  /** Copy shown instead of the video when the camera cannot start. */
  cameraErrorText: string;
  /** Hide with CSS while a result/manual form is shown; never unmounts the camera. */
  hidden?: boolean;
  /**
   * Rendered width. The default is the 112 px viewfinder chip; the aiming view
   * passes `"100%"` so the same mounted element grows in place.
   */
  width?: number | string;
}

/** i18n keys for the three persisted display modes. */
export const SCANNER_DISPLAY_KEYS: Record<ScannerDisplay, MessageKey> = {
  cover: "scanDisplayCover",
  contain: "scanDisplayContain",
  "scan-area": "scanDisplayScanArea",
};

/**
 * The one camera stage shared by every preset (scanner-surface 01, 02, 05).
 *
 * The wrapper stays the **direct parent** of the `<video>` because Quagga2
 * attaches the camera stream there; hiding is CSS-only so a result or manual
 * form never restarts the camera between scans. The persisted display mode is
 * read from the preferences store, never from props:
 *
 * - `cover` fills the square stage (default),
 * - `contain` shows the whole frame letterboxed with the ROI guide,
 * - `scan-area` shows only the central decode region scaled 2×.
 */
export function ScanStage({
  videoRef,
  scanning,
  cameraError,
  ariaLabel,
  cameraErrorText,
  hidden = false,
  width = 112,
}: ScanStageProps) {
  const display = usePreferencesStore((state) => state.scannerDisplay);
  const scanArea = display === "scan-area";

  return (
    <Box
      data-testid="scan-stage"
      data-mode={display}
      sx={{
        display: hidden ? "none" : "block",
        position: "relative",
        width,
        height: typeof width === "number" ? width : undefined,
        aspectRatio: typeof width === "number" ? undefined : "1/1",
        flexShrink: 0,
        bgcolor: "#111",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {cameraError ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "auto",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.25 }}>
            {cameraErrorText}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Quagga2 attaches the camera to `video.parentElement` and injects
              its own markup there, so this wrapper holds exactly one video and
              the overlay stays outside it (scanner-surface 05). */}
          <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
            <video
              ref={videoRef}
              playsInline
              muted
              aria-label={ariaLabel}
              style={{
                position: "absolute",
                width: scanArea ? "200%" : "100%",
                height: scanArea ? "200%" : "100%",
                left: scanArea ? "-50%" : 0,
                top: scanArea ? "-50%" : 0,
                objectFit: display === "contain" ? "contain" : "cover",
                display: "block",
              }}
            />
          </Box>
          {scanArea ? (
            <Box
              data-testid="scan-area-frame"
              sx={{
                position: "absolute",
                inset: 6,
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: 1,
                pointerEvents: "none",
              }}
            />
          ) : (
            <ScanOverlay scanning={scanning} />
          )}
        </>
      )}
    </Box>
  );
}
